/**
 * Sysadmin Tenant Detail — Memory tab (B2-2).
 *
 * Per project_next_waves_plan_2026-05-12.md §B2-2:
 *   Per-tenant operator visibility into user memory (a person's memories —
 *   user_memories, the one store, ASK-M1 D1 — and agent episode rows). Stats cards + cursor-paginated browse + filter by
 *   user/tag/classification + bulk "Purge for user" with TypedConfirm.
 *
 *   A person's memories are the PERSON's (decisions D1 and Ask Argus B5): the
 *   operator sees how many, under which labels and when last updated — never
 *   the words. The purge deletes them without reading them.
 *
 * Sysadmin-only — the parent layout's audience gate handles auth.
 * The route layer enforces `read:memory_observability` /
 * `purge:memory_observability`; tenant roles never receive either grant
 * (orthogonality invariant, permissions.ts §B2-2).
 */

import { useMemo, useState } from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import {
  AlertCircle, Brain, ClipboardList, Filter, Loader2, Tag as TagIcon,
  Trash2, User as UserIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import { TypedConfirmDialog } from '@/components/ui/typed-confirm-dialog';
import {
  useBulkPurgeUserMemory,
  useTenantMemoryEpisodes,
  useTenantPersonMemorySummary,
  isPersonId,
  useTenantMemoryStats,
  type EpisodeFilters,
} from '@/lib/use-sysadmin-memory';

export const Route = createFileRoute('/sysadmin/tenants/$tenantId/memory')({
  component: TenantMemoryTab,
});

function TenantMemoryTab() {
  const { tenantId } = useParams({
    from: '/sysadmin/tenants/$tenantId/memory',
  });
  const [activeSubTab, setActiveSubTab] = useState<'episodes' | 'memories'>('episodes');

  return (
    <div className="space-y-5">
      <StatsHeader tenantId={tenantId} />

      <Tabs
        value={activeSubTab}
        onValueChange={(v) => setActiveSubTab(v as 'episodes' | 'memories')}
        className="space-y-3"
      >
        <TabsList>
          <TabsTrigger value="episodes" className="gap-2">
            <ClipboardList className="h-4 w-4" />
            Episode memories
          </TabsTrigger>
          <TabsTrigger value="memories" className="gap-2">
            <UserIcon className="h-4 w-4" />
            A person's memories
          </TabsTrigger>
        </TabsList>

        <TabsContent value="episodes" className="space-y-3">
          <EpisodeBrowser tenantId={tenantId} />
        </TabsContent>

        <TabsContent value="memories" className="space-y-3">
          <PersonMemoryBrowser tenantId={tenantId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─── Stats header (six cards + top users + top tags) ────────────────────────

function StatsHeader({ tenantId }: { tenantId: string }) {
  const { data, isLoading, error } = useTenantMemoryStats(tenantId);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-6 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading memory stats…
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-destructive">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            {error?.message ?? 'Failed to load memory stats.'}
          </div>
        </CardContent>
      </Card>
    );
  }

  const { stats, topUsers, topTags } = data;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatTile
          label="Memories"
          value={stats.profile.count}
          subValue={`${formatBytes(stats.profile.totalChars)} total`}
        />
        <StatTile
          label="Episodes"
          value={stats.episode.count}
          subValue={`${stats.episode.withEmbedding} embedded`}
        />
        <StatTile
          label="Distinct users"
          value={stats.distinctUsersMax}
        />
        <StatTile
          label="Avg episode size"
          value={formatBytes(stats.episode.avgChars)}
        />
        <StatTile
          label="Oldest memory"
          value={formatRelative(
            earliest(stats.profile.oldestAt, stats.episode.oldestAt),
          )}
        />
        <StatTile
          label="Newest memory"
          value={formatRelative(
            latest(stats.profile.newestAt, stats.episode.newestAt),
          )}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <UserIcon className="h-4 w-4" />
              Top users by memory count
            </CardTitle>
            <CardDescription>
              Sorted by combined (memory + episode) row count.
            </CardDescription>
          </CardHeader>
          <CardContent className="px-0">
            {topUsers.length === 0 ? (
              <div className="px-6 py-4 text-sm text-muted-foreground">
                No memory data yet.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead className="text-right">Memories</TableHead>
                    <TableHead className="text-right">Episodes</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topUsers.map((u) => (
                    <TableRow key={u.userId}>
                      <TableCell className="text-xs">
                        <div className="font-medium">
                          {u.userEmail ?? <span className="text-muted-foreground italic">(unknown user)</span>}
                        </div>
                        <div className="text-muted-foreground font-mono">{u.userId}</div>
                      </TableCell>
                      <TableCell className="text-xs text-right tabular-nums">
                        {u.profileCount}
                      </TableCell>
                      <TableCell className="text-xs text-right tabular-nums">
                        {u.episodeCount}
                      </TableCell>
                      <TableCell className="text-xs text-right tabular-nums font-semibold">
                        {u.totalCount}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <TagIcon className="h-4 w-4" />
              Top tags
            </CardTitle>
            <CardDescription>
              Tag occurrence across all episode memories for this tenant.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {topTags.length === 0 ? (
              <div className="text-sm text-muted-foreground py-2">
                No tags yet.
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {topTags.map((t) => (
                  <Badge key={t.tag} variant="secondary" className="font-mono text-xs">
                    {t.tag}
                    <span className="ml-1 text-muted-foreground">×{t.count}</span>
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatTile({
  label, value, subValue,
}: { label: string; value: number | string; subValue?: string }) {
  return (
    <Card>
      <CardContent className="py-3 px-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-xl font-semibold tabular-nums mt-1">{value}</div>
        {subValue && (
          <div className="text-xs text-muted-foreground mt-0.5">{subValue}</div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Episode browser ────────────────────────────────────────────────────────

function EpisodeBrowser({ tenantId }: { tenantId: string }) {
  const [filters, setFilters] = useState<EpisodeFilters>({ limit: 50 });
  const { data, isLoading, error } = useTenantMemoryEpisodes(tenantId, filters);
  const [purgeOpen, setPurgeOpen] = useState<{
    userId: string;
    userEmail: string | null;
  } | null>(null);

  const items = data?.items ?? [];

  return (
    <div className="space-y-3">
      <EpisodeFiltersBar filters={filters} onChange={setFilters} />

      <Card>
        <CardContent className="px-0">
          {isLoading ? (
            <div className="px-6 py-6 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading episodes…
            </div>
          ) : error ? (
            <div className="px-6 py-6 text-sm text-destructive">
              {error.message}
            </div>
          ) : items.length === 0 ? (
            <div className="px-6 py-10 text-sm text-muted-foreground text-center">
              No episode memories match the current filters.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Content (preview)</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>Classification</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                      {new Date(row.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-xs">
                      <div className="font-medium">{row.userEmail ?? '—'}</div>
                      <div className="text-muted-foreground font-mono">
                        {row.userId.slice(0, 8)}…
                      </div>
                    </TableCell>
                    <TableCell className="text-xs max-w-md">
                      <div className="line-clamp-2">{row.content}</div>
                    </TableCell>
                    <TableCell className="text-xs">
                      <div className="flex flex-wrap gap-1">
                        {row.tags.length === 0 ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          row.tags.slice(0, 4).map((t) => (
                            <Badge key={t} variant="outline" className="text-[10px] font-mono">
                              {t}
                            </Badge>
                          ))
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      <ClassificationBadge
                        sensitivity={row.classificationAtWrite.sensitivity}
                        exportControl={row.classificationAtWrite.exportControl}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                        title={`Purge all memory for ${row.userEmail ?? row.userId}`}
                        onClick={() =>
                          setPurgeOpen({ userId: row.userId, userEmail: row.userEmail })
                        }
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {data?.nextCursor && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setFilters((f) => ({ ...f, cursor: data.nextCursor ?? undefined }))
            }
          >
            Load more
          </Button>
        </div>
      )}

      {purgeOpen && (
        <PurgeUserDialog
          tenantId={tenantId}
          user={purgeOpen}
          onClose={() => setPurgeOpen(null)}
        />
      )}
    </div>
  );
}

// ─── A person's memories: counts and labels, never content ─────────────────

function PersonMemoryBrowser({ tenantId }: { tenantId: string }) {
  const [userId, setUserId] = useState('');
  const { data, isLoading, error } = useTenantPersonMemorySummary(tenantId, userId);
  const [purgeOpen, setPurgeOpen] = useState<{
    userId: string;
    userEmail: string | null;
  } | null>(null);

  const summary = data?.summary;
  const validId = isPersonId(userId);

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="py-3 px-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Person's user UUID (from Top users)…"
              className="max-w-md font-mono text-xs"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            />
            {validId && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1 text-destructive hover:bg-destructive/10"
                title="Purge all memory for this person"
                onClick={() => setPurgeOpen({ userId: userId.trim(), userEmail: null })}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Purge
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="px-0">
          {!validId ? (
            <div className="px-6 py-10 text-sm text-muted-foreground text-center">
              Enter a person's user id to see how many memories Argus holds for them.
            </div>
          ) : isLoading ? (
            <div className="px-6 py-6 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading memories…
            </div>
          ) : error ? (
            <div className="px-6 py-6 text-sm text-destructive">{error.message}</div>
          ) : !summary || summary.count === 0 ? (
            <div className="px-6 py-10 text-sm text-muted-foreground text-center">
              No memories for this person.
            </div>
          ) : (
            <>
              <div className="px-6 pb-2 text-xs text-muted-foreground">
                {summary.count} memor{summary.count === 1 ? 'y' : 'ies'}
                {summary.lastUpdatedAt
                  ? ` · last updated ${new Date(summary.lastUpdatedAt).toLocaleString()}`
                  : ''}
                {' · '}only the person reads their memories, on their Memories page.
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Classification</TableHead>
                    <TableHead className="text-right">Memories</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary.labels.map((l) => (
                    <TableRow key={`${l.sensitivity}|${l.exportControl}`}>
                      <TableCell className="text-xs">
                        <span className="font-mono uppercase text-[10px]">{l.sensitivity} · {l.exportControl}</span>
                      </TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{l.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>

      {purgeOpen && (
        <PurgeUserDialog
          tenantId={tenantId}
          user={purgeOpen}
          onClose={() => setPurgeOpen(null)}
        />
      )}
    </div>
  );
}

// ─── Episode filter bar ─────────────────────────────────────────────────────

function EpisodeFiltersBar({
  filters, onChange,
}: {
  filters: EpisodeFilters;
  onChange: (next: EpisodeFilters) => void;
}) {
  return (
    <Card>
      <CardContent className="py-3 px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">User UUID</Label>
            <Input
              placeholder="e.g. 1a2b3c4d-…"
              className="font-mono text-xs h-8"
              value={filters.userId ?? ''}
              onChange={(e) =>
                onChange({
                  ...filters,
                  userId: e.target.value || undefined,
                  cursor: undefined,
                })
              }
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Tag</Label>
            <Input
              placeholder="e.g. preference"
              className="text-xs h-8"
              value={filters.tag ?? ''}
              onChange={(e) =>
                onChange({
                  ...filters,
                  tag: e.target.value || undefined,
                  cursor: undefined,
                })
              }
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Sensitivity</Label>
            <Select
              value={filters.sensitivity ?? 'all'}
              onValueChange={(v) =>
                onChange({
                  ...filters,
                  sensitivity:
                    v === 'all' ? undefined : (v as EpisodeFilters['sensitivity']),
                  cursor: undefined,
                })
              }
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="unclassified">Unclassified</SelectItem>
                <SelectItem value="cui">CUI</SelectItem>
                <SelectItem value="restricted">Restricted</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Export control</Label>
            <Select
              value={filters.exportControl ?? 'all'}
              onValueChange={(v) =>
                onChange({
                  ...filters,
                  exportControl:
                    v === 'all'
                      ? undefined
                      : (v as EpisodeFilters['exportControl']),
                  cursor: undefined,
                })
              }
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="itar">ITAR</SelectItem>
                <SelectItem value="ear">EAR</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Classification badge ───────────────────────────────────────────────────

function ClassificationBadge({
  sensitivity, exportControl,
}: { sensitivity?: string; exportControl?: string }) {
  const s = sensitivity ?? 'unclassified';
  const e = exportControl ?? 'none';
  // Plain text when both are baseline so the cell isn't noisy.
  if (s === 'unclassified' && e === 'none') {
    return <span className="text-muted-foreground text-[10px]">—</span>;
  }
  return (
    <div className="flex flex-col gap-0.5">
      {s !== 'unclassified' && (
        <Badge variant="outline" className="text-[10px] font-mono uppercase">
          {s}
        </Badge>
      )}
      {e !== 'none' && (
        <Badge variant="outline" className="text-[10px] font-mono uppercase">
          {e}
        </Badge>
      )}
    </div>
  );
}

// ─── Purge dialog ───────────────────────────────────────────────────────────

function PurgeUserDialog({
  tenantId, user, onClose,
}: {
  tenantId: string;
  user: { userId: string; userEmail: string | null };
  onClose: () => void;
}) {
  const purge = useBulkPurgeUserMemory(tenantId);

  // Use the email when available; otherwise the UUID. The TypedConfirm
  // requires the operator to type this verbatim.
  const confirmPhrase = useMemo(
    () => user.userEmail ?? user.userId,
    [user],
  );

  return (
    <TypedConfirmDialog
      open={true}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      variant="danger"
      title="Bulk purge user memory"
      description={`This will wipe BOTH every memory Argus holds about ${confirmPhrase} AND every agent episode memory of theirs.`}
      consequences={[
        'Every memory of this person (the same rows their Memories page shows) is DELETED',
        'All agent_memories rows for this user (across every agent) are DELETED',
        'A PLATFORM_TENANT_MEMORY_BULK_PURGE audit row is committed to the platform audit chain',
        'A memory.cleared row naming you is written to the tenant audit',
        'The user will get an empty Ask Argus the next conversation',
        'Cannot be undone — backups are tenant-scoped, not per-user',
      ]}
      confirmPhrase={confirmPhrase}
      confirmLabel="Purge user memory"
      requireReason
      reasonPlaceholder="e.g. user reported memory contained PII / customer requested cleanup / GDPR right-to-be-forgotten…"
      onConfirm={({ reason }) => {
        purge.mutate(
          {
            userId: user.userId,
            reason: (reason ?? '').trim(),
            confirmed: true,
          },
          {
            onSuccess: (res) => {
              toast.success(
                `Purged ${res.purged.memoriesDeleted} memory(ies) + ${res.purged.episodesDeleted} episode(s) (${formatBytes(res.purged.bytesFreed)})`,
              );
              onClose();
            },
            onError: (err) => {
              toast.error(`Purge failed: ${err.message}`);
            },
          },
        );
      }}
    />
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

function earliest(a: string | null, b: string | null): string | null {
  if (!a && !b) return null;
  if (!a) return b;
  if (!b) return a;
  return new Date(a) < new Date(b) ? a : b;
}

function latest(a: string | null, b: string | null): string | null {
  if (!a && !b) return null;
  if (!a) return b;
  if (!b) return a;
  return new Date(a) > new Date(b) ? a : b;
}
